import { ChatStreamEventTypeEnum } from '@/enums/chatBot/ChatStreamEnums.ts';
import type { ChatStreamEvent } from '@/interfaces/chatBot/IChatStreamEvent.ts';

interface IChatStreamParser {
    finish: () => void;
    push: (chunk: Uint8Array) => void;
}

function isRecord (value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isChatStreamEvent (value: unknown): value is ChatStreamEvent {
    if (
        !isRecord(value)
        || value.version !== 1
        || typeof value.type !== 'string'
        || typeof value.conversationId !== 'string'
    ) return false;

    switch (value.type) {
        case ChatStreamEventTypeEnum.Status:
            return typeof value.status === 'string';
        case ChatStreamEventTypeEnum.TextDelta:
            return typeof value.delta === 'string';
        case ChatStreamEventTypeEnum.Error:
            return typeof value.message === 'string';
        case ChatStreamEventTypeEnum.Complete:
            return true;
        default:
            return false;
    }
}

export function parseChatStreamEvent (line: string): ChatStreamEvent {
    const value: unknown = JSON.parse(line);
    if (!isChatStreamEvent(value)) throw new Error('Invalid ChatBot stream event');
    return value;
}

export function createChatStreamParser (onEvent: (event: ChatStreamEvent) => void): IChatStreamParser {
    const decoder = new TextDecoder();
    let bufferedText = '';
    let finished = false;

    function emitCompleteLines (): void {
        let lineEnd = bufferedText.indexOf('\n');
        while (lineEnd >= 0) {
            const line = bufferedText.slice(0, lineEnd).replace(/\r$/, '');
            bufferedText = bufferedText.slice(lineEnd + 1);
            if (line.trim()) onEvent(parseChatStreamEvent(line));
            lineEnd = bufferedText.indexOf('\n');
        }
    }

    function push (chunk: Uint8Array): void {
        if (finished) throw new Error('ChatBot stream parser is already finished');
        bufferedText += decoder.decode(chunk, { stream: true });
        emitCompleteLines();
    }

    function finish (): void {
        if (finished) return;
        finished = true;
        bufferedText += decoder.decode();
        emitCompleteLines();
        if (bufferedText.trim()) onEvent(parseChatStreamEvent(bufferedText));
        bufferedText = '';
    }

    return { finish, push };
}
